import React, { useState } from "react";
import { Loader2, Plus, X, Search } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

/**
 * BadgePicker
 * - Eingabe eines Badge-Codes, automatisches Laden der deutschen Beschreibung aus Habbo-Gamedata
 * - value: Array<{code, name, description}>
 * - onChange(newValue)
 */
const BadgePicker = ({ value = [], onChange, testIdPrefix = "badge" }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const lookup = async () => {
    const c = code.trim().toUpperCase();
    setError("");
    setPreview(null);
    if (!c) return;
    if (value.some((b) => b.code.toUpperCase() === c)) {
      setError("Bereits hinzugefuegt");
      return;
    }
    setLoading(true);
    try {
      const data = await api.badgeGamedata(c);
      setPreview(data);
      if (!data.found) {
        setError("Keine deutsche Beschreibung gefunden – du kannst sie unten manuell anpassen.");
      }
    } catch (err) {
      setError("Fehler beim Laden der Gamedata");
    } finally {
      setLoading(false);
    }
  };

  const add = () => {
    if (!preview) return;
    onChange([...value, {
      code: preview.code,
      name: preview.name || preview.code,
      description: preview.description || "",
    }]);
    setCode("");
    setPreview(null);
    setError("");
  };

  const remove = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (preview) add();
      else lookup();
    }
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-picker`}>
      {/* Eingabezeile */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            value={code}
            onChange={(e) => { setCode(e.target.value); setPreview(null); setError(""); }}
            onKeyDown={onKey}
            placeholder="Badge-Code (z.B. HLW07, 14X11, ADV11)..."
            data-testid={`${testIdPrefix}-input`}
            className="pl-9 bg-[#0f141b] border-white/10 text-zinc-100 placeholder:text-zinc-500"
          />
        </div>
        <Button
          type="button"
          onClick={lookup}
          disabled={loading || !code.trim()}
          data-testid={`${testIdPrefix}-lookup`}
          className="btn-ghost rounded-lg px-3 py-2 text-sm"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pruefen"}
        </Button>
      </div>

      {/* Preview vor dem Hinzufuegen */}
      {preview && (
        <div className="flex items-start gap-3 rounded-lg border border-[#7c4dff]/30 bg-[#7c4dff]/5 p-3" data-testid={`${testIdPrefix}-preview`}>
          <img src={preview.image} alt={preview.code} className="hb-pixel-img h-12 w-12 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-white">{preview.name || preview.code}</div>
            <div className="text-xs text-zinc-400 line-clamp-2">{preview.description || "(Keine Beschreibung in Gamedata)"}</div>
            <div className="mt-1 text-[10px] uppercase tracking-wider text-zinc-500">Code: {preview.code} {preview.found ? "✓ Habbo-Gamedata" : "⚠ Nicht in Gamedata gefunden"}</div>
          </div>
          <Button
            type="button"
            onClick={add}
            data-testid={`${testIdPrefix}-add`}
            className="btn-primary shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold"
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Hinzufuegen
          </Button>
        </div>
      )}

      {error && <div className="text-xs text-[#ffb84d]">{error}</div>}

      {/* Liste hinzugefuegter Badges */}
      {value.length > 0 && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid={`${testIdPrefix}-list`}>
          {value.map((b, i) => (
            <div
              key={`${b.code}-${i}`}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#10141b] p-2"
              data-testid={`${testIdPrefix}-item-${b.code}`}
            >
              <img src={`https://images.habbo.com/c_images/album1584/${b.code}.gif`} alt={b.code} className="hb-pixel-img h-8 w-8 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold text-white">{b.name || b.code}</div>
                <div className="truncate text-[10px] text-zinc-500">{b.code}</div>
              </div>
              <button type="button" onClick={() => remove(i)} aria-label="Entfernen" className="rounded p-1 text-zinc-400 hover:bg-rose-500/20 hover:text-rose-300">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BadgePicker;
